import { useState } from 'react';
import type { TimingSlot } from '../types/microsite.types';

interface TimingPickerProps {
    timings: TimingSlot[];
    onChange: (timings: TimingSlot[]) => void;
}

const DAYS = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 7, label: 'Sunday' },
];

/**
 * TimingPicker component - Manage operating hours for each day
 */
export default function TimingPicker({ timings, onChange }: TimingPickerProps) {
    const [selectedDay, setSelectedDay] = useState<number>(1);
    const [openingTime, setOpeningTime] = useState('09:00');
    const [closingTime, setClosingTime] = useState('21:00');

    const handleAddTiming = () => {
        // Check if day already exists
        if (timings.some((t) => t.day === selectedDay)) {
            alert('Timing for this day already exists');
            return;
        }

        // Create Date objects for opening and closing times
        const openDate = new Date(`1970-01-01T${openingTime}:00`);
        const closeDate = new Date(`1970-01-01T${closingTime}:00`);

        const newTiming: TimingSlot = {
            day: selectedDay,
            openingTime: openDate,
            closingTime: closeDate,
        };

        const updatedTimings = [...timings, newTiming].sort((a, b) => a.day - b.day);
        onChange(updatedTimings);

        // Auto-select the next available day
        const nextAvailableDays = DAYS.filter(
            (d) => !updatedTimings.some((t) => t.day === d.value)
        );
        if (nextAvailableDays.length > 0) {
            setSelectedDay(nextAvailableDays[0].value);
        }
    };

    const handleRemoveTiming = (day: number) => {
        onChange(timings.filter((t) => t.day !== day));
    };

    const formatTime = (date: Date): string => {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    const getDayLabel = (day: number): string => {
        return DAYS.find((d) => d.value === day)?.label || '';
    };

    const availableDays = DAYS.filter((d) => !timings.some((t) => t.day === d.value));

    return (
        <div className="space-y-4">
            {/* Existing Timings */}
            {timings.length > 0 && (
                <div className="space-y-2">
                    {timings.map((timing) => (
                        <div
                            key={timing.day}
                            className="flex items-center justify-between p-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-lg"
                        >
                            <div className="flex items-center gap-4">
                                <span className="font-medium text-[#1e293b] w-24">
                                    {getDayLabel(timing.day)}
                                </span>
                                <span className="text-[#64748b]">
                                    {formatTime(timing.openingTime)} -{' '}
                                    {formatTime(timing.closingTime)}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => handleRemoveTiming(timing.day)}
                                className="p-1.5 text-[#ef4444] hover:bg-[#fee2e2] rounded transition-colors"
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Add New Timing */}
            {availableDays.length > 0 && (
                <div className="p-4 border-2 border-dashed border-[#cbd5e1] rounded-lg space-y-3">
                    <p className="text-sm font-medium text-[#1e293b]">Add Operating Hours</p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <select
                            value={selectedDay}
                            onChange={(e) => setSelectedDay(Number(e.target.value))}
                            className="px-4 py-2 bg-white border border-[#e2e8f0] rounded-lg text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-[#6e64f0]"
                        >
                            {availableDays.map((day) => (
                                <option key={day.value} value={day.value}>
                                    {day.label}
                                </option>
                            ))}
                        </select>

                        <input
                            type="time"
                            value={openingTime}
                            onChange={(e) => setOpeningTime(e.target.value)}
                            className="px-4 py-2 bg-white border border-[#e2e8f0] rounded-lg text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-[#6e64f0]"
                        />

                        <input
                            type="time"
                            value={closingTime}
                            onChange={(e) => setClosingTime(e.target.value)}
                            className="px-4 py-2 bg-white border border-[#e2e8f0] rounded-lg text-[#1e293b] focus:outline-none focus:ring-2 focus:ring-[#6e64f0]"
                        />

                        <button
                            type="button"
                            onClick={handleAddTiming}
                            className="px-4 py-2 bg-[#6e64f0] text-white rounded-lg hover:bg-[#5b54d8] transition-colors font-medium"
                        >
                            Add
                        </button>
                    </div>
                </div>
            )}

            {timings.length === 7 && (
                <p className="text-sm text-[#10b981]">✓ Operating hours configured for all days</p>
            )}
        </div>
    );
}
